import { LogLevel } from '../types';
/**
 * Transport that logs to the console
 */
export class ConsoleTransport {
    /**
     * Creates a new ConsoleTransport
     * @param options Transport options
     */
    constructor(options = {}) {
        this.options = {
            useColors: true,
            includeTimestamps: true,
            includeCorrelationId: true,
            formatJson: false,
            ...options,
        };
    }
    /**
     * Logs a message to the console
     * @param level Log level
     * @param message Log message
     * @param context Log context
     */
    log(level, message, context) {
        const logFn = this.getLogFunction(level);
        const formattedMessage = this.formatMessage(level, message, context);
        logFn(formattedMessage);
    }
    /**
     * Gets inline context information for single-line logging
     * @param context Log context
     * @returns Formatted context string or null
     */
    getInlineContext(context) {
        if (this.options.formatJson) {
            return this.getInlineJsonContext(context);
        }
        return this.getInlineTextContext(context);
    }
    /**
     * Gets context as inline JSON format
     * @param context Log context
     * @returns Formatted JSON context string or null
     */
    getInlineJsonContext(context) {
        const { correlationId: _correlationId, userId: _userId, category: _category, ...rest } = context;
        if (Object.keys(rest).length > 0) {
            return JSON.stringify(rest);
        }
        return null;
    }
    /**
     * Gets context as inline formatted text
     * @param context Log context
     * @returns Formatted context string or null
     */
    getInlineTextContext(context) {
        const relevantContext = this.extractRelevantContext(context);
        if (relevantContext.length > 0) {
            return relevantContext.join(', ');
        }
        return null;
    }
    /**
     * Extracts relevant context information for display
     * @param context Full log context
     * @returns Array of formatted key-value strings
     */
    extractRelevantContext(context) {
        const relevant = [];
        // Fields that are already displayed in the main message - exclude these
        const excludeFields = new Set([
            'timestamp',
            'level',
            'message',
            'correlationId',
            'userId',
            'category',
            'app',
            'context', // NestJS context name
        ]);
        // Process all context fields
        const contextEntries = Object.entries(context).filter(([key]) => !excludeFields.has(key));
        // Add all relevant fields (no priority system needed)
        for (const [key, value] of contextEntries) {
            if (this.shouldIncludeField(key, value)) {
                const formattedValue = this.formatContextValue(key, value);
                if (formattedValue) {
                    relevant.push(`${key}=${formattedValue}`);
                }
            }
        }
        return relevant;
    }
    /**
     * Determines if a field should be included based on generic criteria
     * @param key Field key
     * @param value Field value
     * @returns Whether to include the field
     */
    shouldIncludeField(key, value) {
        if (value === null || value === undefined || value === '') {
            return false;
        }
        if (typeof value === 'boolean') {
            return value === true;
        }
        return true;
    }
    /**
     * Formats a context value for display
     * @param key The context key
     * @param value The context value
     * @returns Formatted string or null if should be excluded
     */
    formatContextValue(key, value) {
        if (value === null || value === undefined) {
            return null;
        }
        return this.formatValue(value, key);
    }
    /**
     * Formats a value for display
     * @param value Value to format
     * @param key Optional key for context-specific formatting
     * @returns Formatted string
     */
    formatValue(value, key) {
        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        if (typeof value === 'number') {
            return this.formatNumber(value, key);
        }
        if (typeof value === 'string') {
            return this.formatString(value);
        }
        if (Array.isArray(value)) {
            return this.formatArray(value);
        }
        if (typeof value === 'object') {
            return this.formatObject(value);
        }
        return String(value);
    }
    /**
     * Formats a number value
     * @param value Number to format
     * @param key Optional key for context-specific formatting
     * @returns Formatted number string
     */
    formatNumber(value, key) {
        const lowerKey = key?.toLowerCase() || '';
        if (lowerKey.includes('duration')) {
            return `${value}ms`;
        }
        return String(value);
    }
    /**
     * Formats a string value
     * @param value String to format
     * @returns Formatted string
     */
    formatString(value) {
        // Removed truncation - show full string for debugging
        return `"${value}"`;
    }
    /**
     * Formats an array for display
     * @param value Array to format
     * @returns Formatted array string
     */
    formatArray(value) {
        if (value.length === 0) {
            return '[]';
        }
        if (value.length <= 3) {
            return `[${value.map(v => this.formatSimpleValue(v)).join(', ')}]`;
        }
        const firstTwo = value.slice(0, 2).map(v => this.formatSimpleValue(v));
        return `[${firstTwo.join(', ')}, +${value.length - 2} more]`;
    }
    /**
     * Formats an object for display
     * @param value Object to format
     * @returns Formatted object string
     */
    formatObject(value) {
        const entries = Object.entries(value);
        if (entries.length === 0) {
            return '{}';
        }
        if (entries.length === 1) {
            const [k, v] = entries[0];
            return `{${k}: ${this.formatSimpleValue(v)}}`;
        }
        // Show up to 3 key-value pairs, then indicate remaining count
        if (entries.length <= 3) {
            const formatted = entries.map(([k, v]) => `${k}: ${this.formatSimpleValue(v)}`).join(', ');
            return `{${formatted}}`;
        }
        // Show first 2 entries and indicate remaining count
        const firstTwo = entries.slice(0, 2).map(([k, v]) => `${k}: ${this.formatSimpleValue(v)}`);
        const remaining = entries.length - 2;
        return `{${firstTwo.join(', ')}, +${remaining} more}`;
    }
    /**
     * Formats a simple value (used within arrays/objects)
     * @param value Value to format
     * @returns Formatted string
     */
    formatSimpleValue(value) {
        if (value === null)
            return 'null';
        if (value === undefined)
            return 'undefined';
        if (typeof value === 'string') {
            // Removed truncation for simple values too - show full string for debugging
            return `"${value}"`;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        // For complex objects, use JSON.stringify with circular reference handling
        return this.safeStringify(value);
    }
    /**
     * Safely stringifies a value with circular reference handling
     * @param value Value to stringify
     * @returns JSON string or fallback representation
     */
    safeStringify(value) {
        try {
            const seen = new WeakSet();
            const result = JSON.stringify(value, (key, val) => {
                if (typeof val === 'object' && val !== null) {
                    if (seen.has(val))
                        return '[Circular]';
                    seen.add(val);
                }
                if (typeof val === 'function')
                    return `[Function: ${val.name || 'anonymous'}]`;
                if (val === undefined)
                    return '[undefined]';
                if (typeof val === 'symbol')
                    return `[Symbol: ${val.toString()}]`;
                return val;
            });
            return result && result.length > 500 ? `${result.substring(0, 497)}...` : result || '[object]';
        }
        catch {
            if (value && typeof value === 'object') {
                try {
                    const name = value.constructor?.name || 'Object';
                    return `[${name}]`;
                }
                catch {
                    return '[object]';
                }
            }
            return String(value);
        }
    }
    /**
     * Gets the appropriate console function for the log level
     * @param level Log level
     * @returns Console function
     */
    getLogFunction(level) {
        switch (level) {
            case LogLevel.ERROR:
                return console.error;
            case LogLevel.WARN:
                return console.warn;
            case LogLevel.INFO:
                return console.info;
            case LogLevel.DEBUG:
            case LogLevel.TRACE:
                return console.debug;
            default:
                return console.log;
        }
    }
    /**
     * Formats a log message with context
     * @param level Log level
     * @param message Log message
     * @param context Log context
     * @returns Formatted message
     */
    formatMessage(level, message, context) {
        const parts = [];
        this.addTimestampToParts(parts);
        this.addLevelToParts(parts, level);
        this.addLoggerNameToParts(parts, context);
        this.addMessageToParts(parts, message, context);
        return parts.join(':');
    }
    /**
     * Adds timestamp to parts array if enabled
     * @param parts Parts array
     */
    addTimestampToParts(parts) {
        if (this.options.includeTimestamps) {
            parts.push(this.formatTimestampForService(new Date()));
        }
    }
    /**
     * Adds formatted level to parts array
     * @param parts Parts array
     * @param level Log level
     */
    addLevelToParts(parts, level) {
        if (this.options.useColors) {
            parts.push(this.formatLevelWithColor(level));
        }
        else {
            parts.push(this.formatLevelForService(level));
        }
    }
    /**
     * Adds logger name to parts array
     * @param parts Parts array
     * @param context Log context
     */
    addLoggerNameToParts(parts, context) {
        const loggerName = (typeof context?.context === 'string' ? context.context : null) || context?.category?.toString() || 'SYSTEM';
        parts.push(this.options.useColors ? this.colorizeLoggerName(loggerName) : loggerName);
    }
    /**
     * Adds message with context to parts array
     * @param parts Parts array
     * @param message Log message
     * @param context Log context
     */
    addMessageToParts(parts, message, context) {
        let fullMessage = message;
        // Add correlation ID if available and enabled
        if (this.options.includeCorrelationId && context?.correlationId) {
            fullMessage = `[${context.correlationId}] ${fullMessage}`;
        }
        if (context) {
            const contextInfo = this.getInlineContext(context);
            if (contextInfo) {
                fullMessage = `${fullMessage} ${contextInfo}`;
            }
        }
        parts.push(fullMessage);
    }
    /**
     * Formats a timestamp
     * @param date Date to format
     * @returns Formatted timestamp
     */
    formatTimestamp(date) {
        return `[${date.toISOString()}]`;
    }
    /**
     * Formats a timestamp for service format (without brackets)
     * @param date Date to format
     * @returns Formatted timestamp
     */
    formatTimestampForService(date) {
        return date.toISOString();
    }
    /**
     * Formats a log level for service format (without brackets and colors)
     * @param level Log level
     * @returns Formatted log level
     */
    formatLevelForService(level) {
        return level;
    }
    /**
     * Formats a log level with color but without brackets for service format
     * @param level Log level
     * @returns Formatted log level with color
     */
    formatLevelWithColor(level) {
        if (!this.options.useColors) {
            return level;
        }
        const colors = {
            reset: '\x1b[0m',
            red: '\x1b[31m',
            yellow: '\x1b[33m',
            green: '\x1b[32m',
            blue: '\x1b[34m',
            gray: '\x1b[90m',
        };
        let color;
        switch (level) {
            case LogLevel.ERROR:
                color = colors.red;
                break;
            case LogLevel.WARN:
                color = colors.yellow;
                break;
            case LogLevel.INFO:
                color = colors.green;
                break;
            case LogLevel.DEBUG:
                color = colors.blue;
                break;
            case LogLevel.TRACE:
                color = colors.gray;
                break;
            default:
                color = colors.reset;
        }
        return `${color}${level}${colors.reset}`;
    }
    /**
     * Colorizes the logger name for better visibility
     * @param loggerName Logger name
     * @returns Colorized logger name
     */
    colorizeLoggerName(loggerName) {
        if (!this.options.useColors) {
            return loggerName;
        }
        const colors = {
            reset: '\x1b[0m',
            cyan: '\x1b[36m',
        };
        return `${colors.cyan}${loggerName}${colors.reset}`;
    }
    /**
     * Formats a log level with color if enabled
     * @param level Log level
     * @returns Formatted log level
     */
    formatLevel(level) {
        if (!this.options.useColors) {
            return `[${level}]`;
        }
        const colors = {
            reset: '\x1b[0m',
            red: '\x1b[31m',
            yellow: '\x1b[33m',
            green: '\x1b[32m',
            blue: '\x1b[34m',
            gray: '\x1b[90m',
        };
        let color;
        switch (level) {
            case LogLevel.ERROR:
                color = colors.red;
                break;
            case LogLevel.WARN:
                color = colors.yellow;
                break;
            case LogLevel.INFO:
                color = colors.green;
                break;
            case LogLevel.DEBUG:
                color = colors.blue;
                break;
            case LogLevel.TRACE:
                color = colors.gray;
                break;
            default:
                color = colors.reset;
        }
        return `${color}[${level}]${colors.reset}`;
    }
}
//# sourceMappingURL=console-transport.js.map